import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'gop_pending_registration';

/**
 * PreviewCertificate — mid-funnel emotional payoff between /register submit and
 * the payment / free-October success screens.  Reads gop_pending_registration
 * from localStorage (written by AuthPage right after POST /register succeeds).
 *
 * The certificate mockup rebuilds the PDF layout in pure CSS so parents see
 * their child's name in an aspirational context BEFORE the payment ask.
 */
export default function PreviewCertificate() {
  const navigate = useNavigate();
  const pending = useMemo(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const [flash, setFlash] = useState(null);

  // Fire analytics event once when the page mounts.  Silent failure is fine
  // — we don't block the UX on a logging call.
  useEffect(() => {
    if (!pending?.registration_id) return;
    axios
      .post(`${API}/register/${pending.registration_id}/preview-shown`, {})
      .catch(() => {});
  }, [pending]);

  // If a parent lands here without a pending registration, bounce them home.
  if (!pending || !pending.registration_id) {
    return <Navigate to="/register" replace />;
  }

  const student = (pending.student_name || '').trim();
  const displayName = student || "Your child's";
  const firstName = student ? student.split(' ')[0] : "Your child";

  const onPrimary = () => navigate('/register/continue');
  const onSecondary = () => {
    setFlash("Your spot is saved for 48 hours — check your WhatsApp for a reminder.");
    // Keep the pending state so /register/continue is still reachable, but
    // give the parent a moment to read the flash before we redirect them home.
    setTimeout(() => navigate('/'), 2400);
  };

  // Auto-scale name font: shrinks for long names so we never overflow the card.
  const nameLen = displayName.length;
  const nameFontClass =
    nameLen > 22 ? 'text-3xl sm:text-4xl' :
    nameLen > 16 ? 'text-4xl sm:text-5xl' :
                   'text-5xl sm:text-6xl';

  return (
    <div
      className="min-h-screen px-4 py-8 sm:py-12 bg-gradient-to-b from-amber-50 via-white to-white"
      data-testid="preview-cert-page"
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {/* ---- The certificate mockup (mirrors the real PDF layout) ---- */}
        <article
          className="relative overflow-hidden rounded-xl shadow-2xl border-[6px] border-double border-amber-400 bg-[#fdf6e3]"
          style={{
            backgroundImage:
              "radial-gradient(circle at top left, rgba(201,169,97,0.06) 0, transparent 60%), " +
              "radial-gradient(circle at bottom right, rgba(30,42,74,0.05) 0, transparent 55%)"
          }}
          data-testid="preview-cert-mock"
        >
          {/* Watermark — diagonal "PREVIEW" */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center select-none"
            aria-hidden="true"
            data-testid="preview-cert-watermark"
          >
            <div
              className="text-6xl sm:text-8xl font-black text-slate-900 tracking-widest whitespace-nowrap"
              style={{ transform: 'rotate(-24deg)', opacity: 0.09 }}
            >
              PREVIEW · NOT YET ISSUED
            </div>
          </div>

          <div className="relative px-6 sm:px-10 py-10 sm:py-14 text-center">
            {/* Top-center: Imperial Royal Crest */}
            <img
              src="/brand/crest_imperial.png"
              alt="Genius One Plus Imperial Crest"
              className="mx-auto h-24 sm:h-28 w-auto mb-4"
              data-testid="preview-cert-imperial"
            />

            <h1
              className="text-3xl sm:text-4xl font-serif text-[#1e2a4a] tracking-tight"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Certificate of Excellence
            </h1>
            <div className="mt-2 text-sm sm:text-base font-semibold tracking-widest text-amber-700">
              ★  GENIUS ONE PLUS  ★
            </div>

            <p className="mt-8 sm:mt-10 text-sm sm:text-base text-slate-700 italic">
              This is to certify that
            </p>

            {/* Interpolated student name — calligraphic */}
            <div
              className={`mt-3 font-bold text-amber-700 ${nameFontClass}`}
              style={{
                fontFamily: '"Great Vibes", "Playfair Display", Georgia, cursive',
                lineHeight: 1.1,
                letterSpacing: '0.5px',
              }}
              data-testid="preview-cert-student-name"
            >
              {displayName}
            </div>

            <p className="mt-6 sm:mt-8 text-sm sm:text-base text-slate-700 max-w-xl mx-auto leading-relaxed">
              has demonstrated the character and commitment expected of Grade 5-6
              scholars, and is enrolled in the Genius One Plus academic year.
            </p>

            {/* Bottom row — signature block + small crest seal */}
            <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row items-end justify-between gap-6 text-left">
              <div className="text-xs sm:text-sm text-slate-600 italic">
                <div className="border-t border-slate-400 pt-1 w-52">
                  Signed on behalf of TEC Sri Lanka Worldwide (Pvt) Ltd
                </div>
                <div className="mt-0.5">Colombo, Sri Lanka</div>
              </div>
              <img
                src="/brand/crest_imperial.png"
                alt=""
                aria-hidden="true"
                className="h-14 sm:h-16 w-auto opacity-90"
                data-testid="preview-cert-mini-seal"
              />
            </div>
          </div>

          {/* Footer strip */}
          <div className="bg-[#1e2a4a] text-amber-100 py-2.5 px-4 text-center text-[10px] sm:text-xs uppercase tracking-widest"
               data-testid="preview-cert-footer">
            Genius One Plus — a programme of TEC Sri Lanka Worldwide (Pvt) Ltd
          </div>
        </article>

        {/* ---- Headline + CTAs ---- */}
        <div className="text-center space-y-4" data-testid="preview-cert-cta-block">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">
            <span data-testid="preview-headline-name">{firstName}</span>&rsquo;s certificate is waiting.
          </h2>
          <p className="text-base sm:text-lg text-slate-700 max-w-2xl mx-auto">
            Complete your child&rsquo;s enrollment to reserve this TEC Sri Lanka
            Worldwide programme Certificate of Excellence in their name. The
            certificate becomes official upon Grade 5-6 academic year completion.
          </p>

          {flash && (
            <div
              className="mx-auto max-w-xl rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
              data-testid="preview-cert-flash"
            >
              {flash}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button
              size="lg"
              className="h-14 px-8 text-lg"
              onClick={onPrimary}
              data-testid="preview-primary-cta"
            >
              Reserve My Child&rsquo;s Certificate — Pay LKR 750
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-14 px-8"
              onClick={onSecondary}
              data-testid="preview-secondary-link"
            >
              I&rsquo;ll decide later
            </Button>
          </div>
          <p className="text-xs text-muted-foreground pt-1">
            Registration ID {pending.registration_id.slice(0, 8)}…  ·
            {' '}<Link to="/" className="underline">back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
